import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from '../Components/Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { useAuth } from '../AuthContext';

const POKEAPI = 'https://pokeapi.co/api/v2';
let cachedPokeList = null;

function idFromUrl(url) {
  return parseInt(url.replace(/\/$/, '').split('/').pop(), 10);
}

function spriteUrl(pokemonId) {
  return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemonId}.png`;
}

function smallSprite(pokemonId) {
  return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemonId}.png`;
}

function PokeSearch({ label, value, onChange, selected, onSelect, pokeList }) {
  const suggestions = value.length >= 2 && !selected
    ? pokeList.filter(p => p.name.includes(value.toLowerCase().trim())).slice(0, 6)
    : [];

  return (
    <div className="form-group">
      <label>{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => { onChange(e.target.value); onSelect(null); }}
        
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <div className="pokemon-search-results">
          {suggestions.map(s => {
            const pid = idFromUrl(s.url);
            return (
              <div
                key={s.name}
                className="pokemon-search-result"
                onClick={() => { onSelect({ name: s.name, pokemonId: pid }); onChange(s.name); }}
              >
                <img src={smallSprite(pid)} alt={s.name} className="pokemon-search-img" />
                <div className="pokemon-search-info">
                  <strong style={{ textTransform: 'capitalize' }}>{s.name}</strong>
                  <span>#{String(pid).padStart(3, '0')}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {selected && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem' }}>
          <img
            src={spriteUrl(selected.pokemonId)}
            alt={selected.name}
            style={{ width: 48, height: 48, objectFit: 'contain' }}
            onError={e => { e.target.onerror = null; e.target.src = smallSprite(selected.pokemonId); }}
          />
          <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>{selected.name}</span>
        </div>
      )}
    </div>
  );
}

function PairCard({ pair }) {
  return (
    <div className="soul-link-card">
      {pair.pair_name && <div className="soul-link-pair-label">{pair.pair_name}</div>}
      <div className="soul-link-slots">
        <div className="soul-link-slot">
          <img
            src={spriteUrl(pair.p1_pokemon_id)}
            alt={pair.p1_name}
            className="soul-link-img"
            onError={e => { e.target.onerror = null; e.target.src = smallSprite(pair.p1_pokemon_id); }}
          />
          <span className="soul-link-player">Ghoul</span>
          <span className="soul-link-poke-name">{pair.p1_name}</span>
        </div>
        <span className="soul-link-heart">X</span>
        <div className="soul-link-slot">
          <img
            src={spriteUrl(pair.p2_pokemon_id)}
            alt={pair.p2_name}
            className="soul-link-img"
            onError={e => { e.target.onerror = null; e.target.src = smallSprite(pair.p2_pokemon_id); }}
          />
          <span className="soul-link-player">Arty</span>
          <span className="soul-link-poke-name">{pair.p2_name}</span>
        </div>
      </div>
    </div>
  );
}

function AddPairModal({ onClose, onAdd, pokeList, title, loading, status }) {
  const [pairName, setPairName] = useState('');
  const [p1Search, setP1Search] = useState('');
  const [p1Selected, setP1Selected] = useState(null);
  const [p2Search, setP2Search] = useState('');
  const [p2Selected, setP2Selected] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd({ pairName, p1Selected, p2Selected });
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="modal-title">{title}</h2>
      <form className="modal-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Name</label>
          <input
            type="text"
            value={pairName}
            onChange={e => setPairName(e.target.value)}
            
            autoFocus
            autoComplete="off"
          />
        </div>
        <PokeSearch label="Ghoul" value={p1Search} onChange={setP1Search} selected={p1Selected} onSelect={setP1Selected} pokeList={pokeList} />
        <PokeSearch label="Arty" value={p2Search} onChange={setP2Search} selected={p2Selected} onSelect={setP2Selected} pokeList={pokeList} />
        {status && <p className="form-status">{status}</p>}
        <div className="edit-modal-actions">
          <button type="submit" className="form-submit" disabled={loading || !p1Selected || !p2Selected}>
            {loading ? 'Adding...' : 'Add'}
          </button>
          <button type="button" className="form-submit btn-cancel" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function SoulLinkRun() {
  const { user } = useAuth();
  const { gameId, runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [finalPairs, setFinalPairs] = useState([]);
  const [pokeList, setPokeList] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showFinalAdd, setShowFinalAdd] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addStatus, setAddStatus] = useState('');

  const fetchRun = async () => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/soul_link_runs?id=eq.${runId}&select=*`, {
      headers: supabaseHeaders(false),
    });
    const data = await res.json();
    setRun(data[0] || null);
  };

  const fetchPairs = async () => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/soul_link_pairs?run_id=eq.${runId}&order=created_at.asc&select=*`, {
      headers: supabaseHeaders(false),
    });
    const data = await res.json();
    const all = Array.isArray(data) ? data : [];
    setPairs(all.filter(p => !p.is_final_team));
    setFinalPairs(all.filter(p => p.is_final_team));
  };

  useEffect(() => {
    fetchRun();
    fetchPairs();
  }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cachedPokeList) { setPokeList(cachedPokeList); return; }
    fetch(`${POKEAPI}/pokemon?limit=100000&offset=0`)
      .then(r => r.json())
      .then(data => {
        cachedPokeList = data.results || [];
        setPokeList(cachedPokeList);
      });
  }, []);

  const toggleFinal = async () => {
    const next = !run.is_final;
    setRun(prev => ({ ...prev, is_final: next }));
    const res = await fetch(`${SUPABASE_URL}/rest/v1/soul_link_runs?id=eq.${runId}`, {
      method: 'PATCH',
      headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
      body: JSON.stringify({ is_final: next }),
    });
    if (!res.ok) {
      setRun(prev => ({ ...prev, is_final: !next }));
    }
  };

  const handleAdd = async ({ pairName, p1Selected, p2Selected }, isFinalTeam = false) => {
    if (!p1Selected || !p2Selected) { setAddStatus('Select a Pokémon for both players.'); return; }
    setAddLoading(true);
    setAddStatus('');
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/soul_link_pairs`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify({
          run_id: runId,
          pair_name: pairName || null,
          p1_name: p1Selected.name,
          p1_pokemon_id: p1Selected.pokemonId,
          p2_name: p2Selected.name,
          p2_pokemon_id: p2Selected.pokemonId,
          fainted: false,
          is_final_team: isFinalTeam,
        }),
      });
      if (!res.ok) throw new Error('Failed to add');
      const data = await res.json();
      const newPair = Array.isArray(data) ? data[0] : data;
      if (isFinalTeam) setFinalPairs(prev => [...prev, newPair]);
      else setPairs(prev => [...prev, newPair]);
      if (isFinalTeam) setShowFinalAdd(false);
      else setShowAdd(false);
    } catch (err) {
      setAddStatus(`Error: ${err.message}`);
    } finally {
      setAddLoading(false);
    }
  };

  if (!run) return null;

  return (
    <div className="app-container">
      <div className="header">
        <h1>{run.name}</h1>
        <div className="controls">
          <button onClick={() => navigate(`/pokemon/soullink/${gameId}`)} className="header-nav-btn">←</button>
          {user && (
            <label className="soul-link-final-toggle">
              <input type="checkbox" checked={!!run.is_final} onChange={toggleFinal} />
              Final Run
            </label>
          )}
          {user && run.is_final && finalPairs.length < 6 && (
            <button onClick={() => { setAddStatus(''); setShowFinalAdd(true); }}>Final Team +</button>
          )}
          {user && <button onClick={() => { setAddStatus(''); setShowAdd(true); }}>Add +</button>}
        </div>
      </div>

      {run.is_final && (
        <div className="soul-link-final-section">
          <div className="soul-link-final-header">
            <span>Final Team</span>
            {user && finalPairs.length < 6 && (
              <button onClick={() => { setAddStatus(''); setShowFinalAdd(true); }} className="soul-link-final-add">
                Add +
              </button>
            )}
          </div>
          <div className="soul-link-grid">
            {finalPairs.map(pair => (
              <PairCard key={pair.id} pair={pair} />
            ))}
            {finalPairs.length === 0 && <p style={{ color: '#888', fontSize: '0.9rem' }}>No final team yet.</p>}
          </div>
        </div>
      )}

      <div className="soul-link-grid" style={{ marginTop: '1rem' }}>
        {pairs.map(pair => (
          <PairCard key={pair.id} pair={pair} />
        ))}
      </div>

      {pairs.length === 0 && <p className="no-results">Geen ene tering hier man.</p>}

      {showAdd && (
        <AddPairModal
          title="Add Pair"
          onClose={() => setShowAdd(false)}
          onAdd={(vals) => handleAdd(vals, false)}
          pokeList={pokeList}
          loading={addLoading}
          status={addStatus}
        />
      )}

      {showFinalAdd && (
        <AddPairModal
          title="Add to Final Team"
          onClose={() => setShowFinalAdd(false)}
          onAdd={(vals) => handleAdd(vals, true)}
          pokeList={pokeList}
          loading={addLoading}
          status={addStatus}
        />
      )}
    </div>
  );
}

export default SoulLinkRun;
